"use client";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { FC } from "react";
import Button from "@lib/elements/Button/Button";
import { useRouter } from "next/navigation";
import TeamLogo from "@error-boundary/TeamLogo";
import NotFoundIllustration from "@error-boundary/NotFoundIllustration";
import "@error-boundary/ErrorBoundary.styles.scss";
import {
  type ErrorBoundaryPropsType,
  type HandleButtonOnClickType,
  Error,
} from "@error-boundary/errorBoundary.types";
import { isBrowser } from "@utils/isDoc";

const ErrorBoundary: FC<ErrorBoundaryPropsType> = ({ error, pageAction }) => {
  const router: AppRouterInstance = useRouter();

  // Base error message
  let errorMessage = "Please try again";
  // Error message based on object
  if (error === Error.Console)
    errorMessage = "Something went wrong, please try again";
  // Error message based on route
  if (error === Error.Route) errorMessage = "404 | Page Not Found";

  // This function will help to redirect / reload
  const handleButtonOnClick: HandleButtonOnClickType = (action) => () => {
    // To redirect to home
    if (action === "redirect") {
      router.push(`/`);
      return;
    }
    // Reload current page
    if (isBrowser()) {
      window?.location?.reload();
    }
  };

  return (
    <section data-testid="error-boundary" className="error-container">
      {/* The 404 gets the team's error scene: the ball of yarn rolled away */}
      {error === Error.Route && (
        <>
          <TeamLogo />
          <NotFoundIllustration />
        </>
      )}
      {/* Error message details */}
      <h1 className="error">{errorMessage}</h1>
      {/* If required add button to redirect / reload */}
      {pageAction && (
        <Button
          label={
            pageAction === "redirect"
              ? "Return Home"
              : pageAction === "reload"
                ? "Reload Current Page"
                : ""
          }
          color={"primary"}
          onClick={handleButtonOnClick(pageAction)}
        />
      )}
    </section>
  );
};

export default ErrorBoundary;
